import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { NgModel } from '@angular/forms';
import { ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { safeJsonClone, safeJsonParse, safeJsonStringify } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { EbwModalService } from '@ebw-monorepo/modal';
import { DebounceUtility } from '@ebw-monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import { PsaService } from '../../../../services/psa.service';
import { mapEventsToBulkDto } from './risk-matrix.mapper';

export interface RiskMatrixEvent {
  id: string;
  schema_idropotabile_id: string;
  evento_rischio_id: string;
  probabilita: number;
  lavorato: boolean;
  attivo: boolean;
  note: string | null;
  tenant_id: string;
  evento: {
    id: string;
    gravita: number;
    pericolo_chimico: boolean;
    pericolo_fisico: boolean;
    pericolo_biologico: boolean;
    pericolo_quantitativo: boolean;
    tipologia: string;
    nome: string;
    descrizione: string;
    tenant_id: string;
  };
  actions: RiskMatrixAction[];
  // campi UI-only
  _isNew?: boolean;
}

export interface RiskMatrixAction {
  id: string;
  rel_entity_evento_rischio_id: string;
  azione_mitigatrice_id: string;
  stato: 0 | 1 | 2; // 0=Non Attiva, 1=Pianificata, 2=Attiva
  riduzione_probabilita: number | null;
  responsabile: string | null;
  pianificazione: string | null;
  avanzamento: number | null;
  costi: number | null;
  validazione: string | null;
  tipologia_di_monitoraggio: string | null;
  parametro_monitorato: string | null;
  tenant_id: string;
  azione: {
    id: string;
    nome: string;
    descrizione: string;
    tenant_id: string;
  };
  // campi UI-only
  _isNew?: boolean;
}

export interface RiskMatrixDialogData {
  entityId: string;
  entityLabel: string;
  tipologia: string;
  events: RiskMatrixEvent[];
}

/**
 * Componente per la gestione bulk della Matrice di Rischio.
 *
 * Struttura tabella:
 *  - mat-table con multiTemplateDataRows
 *  - Row principale: dati evento editabili inline
 *  - Filter row: header secondario con i campi di ricerca
 *  - Detail row: azioni mitigatrici con animazione @detailExpand
 *
 * @author Tommaso Lazzaretti
 */
@Component({
  selector: 'ebw-monorepo-risk-matrix',
  templateUrl: './risk-matrix.component.html',
  styleUrls: ['./risk-matrix.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', overflow: 'hidden' })),
      state('expanded', style({ height: '*', overflow: 'hidden' })),
      transition('expanded <=> collapsed', animate('220ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class RiskMatrixComponent implements OnInit, OnDestroy {
  /** Mostra il bordo rosso di errore appena il campo diventa "dirty", senza aspettare il blur */
  public matcher = new ShowOnDirtyErrorStateMatcher();

  // ---- Table config --------------------------------------------------------

  public displayedColumns: string[] = [
    'select',
    'expand',
    'active',
    'worked',
    'hazards',
    'name',
    'description',
    'probability',
    'gravity',
    'rowActions'
  ];

  /**
   * Colonne della filter row — stesso ordine di displayedColumns,
   * con prefisso "filter-" per la registrazione nel mat-table
   */
  public filterColumns: string[] = [
    'filter-select',
    'filter-expand',
    'filter-active',
    'filter-worked',
    'filter-hazards',
    'filter-name',
    'filter-description',
    'filter-probability',
    'filter-gravity',
    'filter-rowActions'
  ];

  /** Colonna fantasma per la detail row */
  public detailColumns: string[] = ['expandedDetail'];

  /** Colonne della sotto-tabella azioni mitigatrici */
  public mitigationColumns: string[] = [
    'mit_status',
    'mit_name',
    'mit_description',
    'mit_efficacy',
    'mit_responsible',
    'mit_planning',
    'mit_progress',
    'mit_cost',
    'mit_validation',
    'mit_actions'
  ];

  public dataSource = new MatTableDataSource<RiskMatrixEvent>([]);

  /** Eventi attualmente espansi (più righe possono essere aperte insieme) */
  public expandedEvents = new Set<RiskMatrixEvent>();

  /** Eventi selezionati per le azioni in blocco (Test Case 7 HLD) */
  public selectedEvents = new SelectionModel<RiskMatrixEvent>(true, []);
  public readonly bulkGravityOptions = [1, 2, 3, 4, 5];
  public bulkGravity: number | null = null;

  // ---- Filters -------------------------------------------------------------

  public filterName = '';
  public filterDescription = '';
  public filterProbability: number | string | null = null;
  public filterGravity: number | string | null = null;
  public filterActive: boolean | null = null;
  public filterWorked: boolean | null = null;

  // ---- State ---------------------------------------------------------------

  public isLoading = false;
  private originalEvents: RiskMatrixEvent[] = [];

  // ---- Select options ------------------------------------------------------

  public readonly gravityOptions = [1, 2, 3, 4, 5];
  public readonly probabilityOptions = [1, 2, 3, 4, 5];
  /** Valore del filtro che seleziona gli eventi con probabilita'/gravita' non ancora valutata (campo vuoto) */
  public readonly notEvaluatedFilter = 'not_evaluated';

  private _filterDebounce = new DebounceUtility(300);

  // --------------------------------------------------------------------------

  constructor(
    @Inject(MAT_DIALOG_DATA) public riskMatrixDialogData: RiskMatrixDialogData,
    private _dialogRef: MatDialogRef<RiskMatrixComponent>,
    private _ebwModalService: EbwModalService,
    private _translateService: TranslateService,
    private _psaService: PsaService
  ) {
    this._filterDebounce.debouncedSubject.subscribe(() => this.applyFilter());
  }

  ngOnInit(): void {
    // riskMatrixDialogData.events arriva già isolato dai dati del chiamante:
    // EbwModalService.openCustomModal() fa già un deep clone dell'intero
    // payload prima di passarlo qui. Clonarlo di nuovo per originalEvents
    // era un secondo giro di JSON.stringify/parse ridondante e sincrono,
    // proprio nell'istante in cui il dialog deve aprirsi — basta un solo
    // clone, spostato sulla copia di lavoro (dataSource) invece che sul
    // baseline usato per il dirty-check.
    this.originalEvents = this.riskMatrixDialogData.events;
    this._initDataSource(safeJsonClone(this.riskMatrixDialogData.events));
  }

  ngOnDestroy(): void {
    this._filterDebounce.complete();
  }

  // ---- DataSource & filter -------------------------------------------------

  private _initDataSource(events: RiskMatrixEvent[]): void {
    this.dataSource.data = events;
    this.dataSource.filterPredicate = (data: RiskMatrixEvent, filter: string) => {
      const f = safeJsonParse(filter, {} as any);
      const matchName = !f.name || data.evento.nome.toLowerCase().includes(f.name.toLowerCase());
      const matchDesc = !f.desc || data.evento.descrizione.toLowerCase().includes(f.desc.toLowerCase());
      const matchScore = (value: number | null, filterValue: number | string | null): boolean =>
        filterValue == null || (filterValue === this.notEvaluatedFilter ? value == null : value === filterValue);
      const matchGrav = matchScore(data.evento.gravita, f.gravity);
      const matchProb = matchScore(data.probabilita, f.probability);
      const matchActive = f.active == null || data.attivo === f.active;
      const matchWorked = f.worked == null || data.lavorato === f.worked;
      return matchName && matchDesc && matchGrav && matchProb && matchActive && matchWorked;
    };
  }

  applyFilter(): void {
    this.dataSource.filter =
      safeJsonStringify({
        name: this.filterName,
        desc: this.filterDescription,
        gravity: this.filterGravity,
        probability: this.filterProbability,
        active: this.filterActive,
        worked: this.filterWorked
      }) ?? '';
  }

  onFilterChange(): void {
    this._filterDebounce.trigger();
  }

  // ---- Computed getters ----------------------------------------------------

  get filteredEvents(): RiskMatrixEvent[] {
    return this.dataSource.filteredData;
  }

  get isDirty(): boolean {
    const current = safeJsonStringify(this.dataSource.data);
    return current === null || current !== safeJsonStringify(this.originalEvents);
  }

  /** Impedisce il salvataggio di eventi nuovi senza nome/descrizione/gravita' e di azioni nuove senza nome (la descrizione è facoltativa) */
  get hasIncompleteRows(): boolean {
    return this.dataSource.data.some(
      (event) =>
        (event._isNew && (!event.evento.nome?.trim() || !event.evento.descrizione?.trim() || this._isEmpty(event.evento.gravita))) ||
        (event.actions ?? []).some((action) => action._isNew && !action.azione.nome?.trim())
    );
  }

  /**
   * Impedisce il salvataggio con valori non validi (scritti a mano nei campi numerici):
   * probabilita' e gravita' intere 1-5, riduzione e avanzamento 0-100, costi >= 0. I campi vuoti
   * sono "non valutati" e non bloccano il salvataggio. I campi sbagliati sono evidenziati in
   * rosso dai validatori dell'input.
   */
  get hasInvalidValues(): boolean {
    const inRange = (value: number | null, min: number, max = Infinity, intero = false): boolean =>
      this._isEmpty(value) || (value >= min && value <= max && (!intero || Number.isInteger(value)));

    return this.dataSource.data.some(
      (event) =>
        !inRange(event.probabilita, 1, 5, true) ||
        !inRange(event.evento.gravita, 1, 5, true) ||
        (event.actions ?? []).some(
          (action) => !inRange(action.riduzione_probabilita, 0, 100) || !inRange(action.avanzamento, 0, 100) || !inRange(action.costi, 0)
        )
    );
  }

  /** Campo numerico non compilato: un input svuotato a mano vale '' invece di null */
  private _isEmpty(value: number | null): boolean {
    return value === null || value === undefined || (value as unknown) === '';
  }

  // ---- Expand/Collapse -----------------------------------------------------

  toggleExpand(event: RiskMatrixEvent): void {
    if (this.expandedEvents.has(event)) {
      this.expandedEvents.delete(event);
    } else {
      this.expandedEvents.add(event);
    }
  }

  isExpanded(event: RiskMatrixEvent): boolean {
    return this.expandedEvents.has(event);
  }

  // ---- Lavorato (icona e blocco modifiche recuperati dal vecchio accordion collassabile) --

  /** Un evento lavorato non è più modificabile — stesso vincolo del vecchio accordion. */
  isEventLocked(event: RiskMatrixEvent): boolean {
    return !!event.lavorato;
  }

  /** Un evento può essere marcato come lavorato solo se è attivo (stesso vincolo di prima). */
  canToggleWorked(event: RiskMatrixEvent): boolean {
    return event.lavorato || event.attivo;
  }

  toggleWorked(event: RiskMatrixEvent): void {
    if (!this.canToggleWorked(event)) {
      return;
    }
    event.lavorato = !event.lavorato;
  }

  // ---- Pericoli (prima erano solo icone in sola lettura, sempre false per un
  // evento nuovo perché non c'era alcun modo di impostarle) -----------------

  toggleHazard(event: RiskMatrixEvent, key: 'pericolo_chimico' | 'pericolo_fisico' | 'pericolo_biologico' | 'pericolo_quantitativo'): void {
    if (this.isEventLocked(event)) {
      return;
    }
    event.evento[key] = !event.evento[key];
  }

  // ---- Stato azione mitigatrice (icone recuperate dal vecchio accordion collassabile) --

  getStatoIcon(stato: 0 | 1 | 2): string {
    switch (stato) {
      case 2:
        return 'check_circle';
      case 1:
        return 'event_available';
      default:
        return 'block';
    }
  }

  getStatoColor(stato: 0 | 1 | 2): string {
    return stato === 0 ? '' : 'primary';
  }

  /** Nel pannello delle opzioni le icone sono nere di default: solo quella
   * che corrisponde al valore attualmente selezionato per la riga è blu. */
  getStatoOptionColor(optionValue: 0 | 1 | 2, selectedStato: 0 | 1 | 2): string {
    return optionValue === selectedStato ? 'primary' : '';
  }

  getStatoTooltipKey(stato: 0 | 1 | 2): string {
    switch (stato) {
      case 2:
        return 'map.sidebar.psa.bulk_risk_matrix.stato_attiva';
      case 1:
        return 'map.sidebar.psa.bulk_risk_matrix.stato_pianificata';
      default:
        return 'map.sidebar.psa.bulk_risk_matrix.stato_non_attiva';
    }
  }

  /** true durante expandAll/collapseAll: mostra l'overlay che oscura la tabella */
  public isExpandingAll = false;
  public isCollapsingAll = false;

  /** Tutte le righe filtrate sono già espanse (stesso insieme che tocca expandAll) */
  get isAllExpanded(): boolean {
    return this.filteredEvents.every((event) => this.expandedEvents.has(event));
  }

  /** Nessuna riga è espansa (collapseAll svuota expandedEvents per intero, non solo le righe filtrate) */
  get isAllCollapsed(): boolean {
    return this.expandedEvents.size === 0;
  }

  expandAll(): void {
    if (this.isExpandingAll || this.isAllExpanded) {
      return;
    }
    this.isExpandingAll = true;
    // Il rAF annidato aspetta che il PRIMO frame (con l'overlay già disegnato)
    // sia stato dipinto prima di eseguire il lavoro sincrono pesante (istanzia
    // tutte le detail-row filtrate in un colpo solo, con tutti i loro
    // mat-form-field) — nessuna durata minima artificiale: dura solo quanto
    // serve davvero.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.filteredEvents.forEach((event) => this.expandedEvents.add(event));
        this.isExpandingAll = false;
      });
    });
  }

  collapseAll(): void {
    if (this.isCollapsingAll || this.isAllCollapsed) {
      return;
    }
    this.isCollapsingAll = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.expandedEvents.clear();
        this.isCollapsingAll = false;
      });
    });
  }

  // ---- Selezione multipla / azioni in blocco (Test Case 7 HLD) -------------

  isAllPagedSelected(): boolean {
    return this.filteredEvents.length > 0 && this.filteredEvents.every((event) => this.selectedEvents.isSelected(event));
  }

  toggleSelectAllPaged(): void {
    if (this.isAllPagedSelected()) {
      this.filteredEvents.forEach((event) => this.selectedEvents.deselect(event));
    } else {
      this.filteredEvents.forEach((event) => this.selectedEvents.select(event));
    }
  }

  bulkActivate(): void {
    this.selectedEvents.selected.forEach((event) => (event.attivo = true));
  }

  bulkDeactivate(): void {
    this.selectedEvents.selected.forEach((event) => (event.attivo = false));
  }

  bulkDelete(): void {
    const toDelete = new Set(this.selectedEvents.selected);
    this.dataSource.data = this.dataSource.data.filter((event) => !toDelete.has(event));
    this.selectedEvents.clear();
  }

  bulkApplyGravity(): void {
    if (this.bulkGravity == null) {
      return;
    }
    this.selectedEvents.selected.forEach((event) => (event.evento.gravita = this.bulkGravity));
  }

  // ---- Toolbar actions -----------------------------------------------------

  cancelChanges(): void {
    if (!this.isDirty) {
      this._resetToOriginal();
      return;
    }

    this._ebwModalService
      .openConfirmModal({
        title: this._translateService.instant('map.sidebar.psa.bulk_risk_matrix.titleConfirm'),
        content: this._translateService.instant('map.sidebar.psa.bulk_risk_matrix.contentConfirm')
      })
      .afterClosed()
      .subscribe((result) => {
        if (result == true) {
          this._resetToOriginal();
        }
      });
  }

  private _resetToOriginal(): void {
    const restored: RiskMatrixEvent[] = safeJsonClone(this.originalEvents);
    this._initDataSource(restored);
    this.filterName = '';
    this.filterDescription = '';
    this.filterGravity = null;
    this.filterProbability = null;
    this.filterActive = null;
    this.filterWorked = null;
    this.expandedEvents.clear();
    this.selectedEvents.clear();
    this.applyFilter();
  }

  clearFilters(): void {
    this.filterName = '';
    this.filterDescription = '';
    this.filterGravity = null;
    this.filterProbability = null;
    this.filterActive = null;
    this.filterWorked = null;
    this.applyFilter();
  }

  // ---- Event CRUD ----------------------------------------------------------

  addNewEvent(): void {
    const newEvent: RiskMatrixEvent = {
      id: null,
      schema_idropotabile_id: this.riskMatrixDialogData.entityId,
      evento_rischio_id: null,
      // vuota = non ancora valutata
      probabilita: null,
      lavorato: false,
      attivo: false,
      note: null,
      tenant_id: null,
      evento: {
        id: null,
        gravita: null,
        pericolo_chimico: false,
        pericolo_fisico: false,
        pericolo_biologico: false,
        pericolo_quantitativo: false,
        tipologia: this.riskMatrixDialogData.tipologia,
        nome: '',
        descrizione: '',
        tenant_id: null
      },
      actions: [],
      _isNew: true
    };
    this.dataSource.data = [newEvent, ...this.dataSource.data];
    this.expandedEvents.add(newEvent);
  }

  deleteEvent(event: RiskMatrixEvent, $event: MouseEvent): void {
    $event.stopPropagation();
    // confronto per riga e non per id: gli eventi non ancora salvati hanno tutti id null
    this.dataSource.data = this.dataSource.data.filter((e) => e !== event);
    this.expandedEvents.delete(event);
    this.selectedEvents.deselect(event);
  }

  // ---- Mitigation actions --------------------------------------------------

  addMitigationAction(event: RiskMatrixEvent, $mouseEvent: MouseEvent): void {
    $mouseEvent.stopPropagation();
    const newAction: RiskMatrixAction = {
      id: null,
      rel_entity_evento_rischio_id: event.id,
      azione_mitigatrice_id: null,
      stato: 0,
      riduzione_probabilita: null,
      responsabile: null,
      pianificazione: null,
      avanzamento: null,
      costi: null,
      validazione: null,
      tipologia_di_monitoraggio: null,
      parametro_monitorato: null,
      tenant_id: null,
      azione: {
        id: null,
        nome: '',
        descrizione: '',
        tenant_id: null
      },
      _isNew: true
    };
    event.actions = [...(event.actions ?? []), newAction];
    this.expandedEvents.add(event);
  }

  deleteMitigationAction(event: RiskMatrixEvent, action: RiskMatrixAction): void {
    // confronto per riga e non per id: le azioni non ancora salvate hanno tutte id null
    event.actions = event.actions.filter((a) => a !== action);
  }

  /**
   * matTooltip si attiva solo su focus/hover, non ad ogni tasto premuto: lo gestiamo
   * a mano ad ogni digitazione così l'avviso compare/scompare subito col bordo rosso.
   */
  onPlanningChange(field: NgModel, tooltip: MatTooltip): void {
    if (field.invalid) {
      tooltip.show();
    } else {
      tooltip.hide();
    }
  }

  // ---- Save / Close --------------------------------------------------------

  /**
   * Salva tutte le modifiche in un'unica chiamata bulk.
   */
  onSave(): void {
    if (this.isLoading || !this.isDirty || this.hasIncompleteRows || this.hasInvalidValues) {
      return;
    }

    const payload = mapEventsToBulkDto(this.riskMatrixDialogData.entityId, this.dataSource.data);
    this.isLoading = true;

    this._psaService._bulkUpsertRiskMatrix(payload).subscribe({
      next: () => {
        this.isLoading = false;
        this._ebwModalService.openSuccessModal({
          title: this._translateService.instant('map.sidebar.psa.bulk_risk_matrix.title'),
          content: this._translateService.instant('map.sidebar.psa.bulk_risk_matrix.save_success')
        });
        this._dialogRef.close('UPDATED');
      },
      error: (error) => {
        this.isLoading = false;
        const outOfRange = error?.error?.code === 'RISK_VALUES_OUT_OF_RANGE';
        this._ebwModalService.openErrorModal({
          title: this._translateService.instant('map.sidebar.psa.bulk_risk_matrix.title'),
          content: outOfRange
            ? this._translateService.instant('map.sidebar.psa.bulk_risk_matrix.values_out_of_range')
            : error?.error?.message ?? this._translateService.instant('map.sidebar.psa.bulk_risk_matrix.save_error')
        });
      }
    });
  }

  onClose(): void {
    this._closeDiscardingChanges();
  }

  onSendToDocuments(): void {
    this._psaService.openMatriceRischio({ id: this.riskMatrixDialogData.entityId, nome: this.riskMatrixDialogData.entityLabel }, true);
  }

  /** La duplicazione copia la matrice salvata: il dialog si chiude, chiedendo conferma se ci sono modifiche non salvate */
  onDuplicate(): void {
    const { entityId, entityLabel, tipologia } = this.riskMatrixDialogData;
    this._closeDiscardingChanges(() => this._psaService.openDuplicatePsa({ id: entityId, search_term: entityLabel, tipologia }, 'MATRICE_RISCHIO'));
  }

  private _closeDiscardingChanges(afterClose?: () => void): void {
    const close = () => {
      this._dialogRef.close();
      afterClose?.();
    };

    if (!this.isDirty) {
      close();
      return;
    }

    this._ebwModalService
      .openConfirmModal({
        title: this._translateService.instant('map.sidebar.psa.bulk_risk_matrix.titleConfirm'),
        content: this._translateService.instant('map.sidebar.psa.bulk_risk_matrix.contentConfirm')
      })
      .afterClosed()
      .subscribe((result) => {
        if (result == true) {
          close();
        }
      });
  }

  onExport(): void {
    this._psaService.openMatriceRischio({ id: this.riskMatrixDialogData.entityId, nome: this.riskMatrixDialogData.entityLabel }, false);
  }
}
